import { motion } from "framer-motion";
import { 
  Users, 
  Plus,
  Search,
  Bell, 
  DollarSign,
  Activity,
  TrendingUp,
  X
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

// Faded element indicator
const Faded = ({ children }) => (
  <div className="relative opacity-20">
    {children}
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <div className="w-full h-px bg-problem/50 rotate-[-8deg]" />
    </div>
  </div>
);

// Mobile dashboard
const SimplifiedMobile = () => (
  <div className="md:hidden bg-zone-diagnosis rounded-md p-2.5 border border-border">
    <div className="flex items-center justify-between mb-2">
      <span className="font-heading font-semibold text-foreground text-xs">Dashboard</span>
      <div className="flex items-center gap-1">
        <button className="p-1 text-muted-foreground"><Search size={11} /></button>
        <button className="p-1 text-muted-foreground"><Bell size={11} /></button>
        <button className="px-1.5 py-0.5 bg-primary text-primary-foreground text-[8px] rounded">New</button>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-1.5 mb-2">
      <div className="bg-card p-1.5 rounded border border-border">
        <p className="font-semibold text-foreground text-[10px]">$24k</p>
        <p className="text-[7px] text-muted-foreground">Revenue</p>
      </div>
      <div className="bg-card p-1.5 rounded border border-border">
        <p className="font-semibold text-foreground text-[10px]">1,429</p>
        <p className="text-[7px] text-muted-foreground">Users</p>
      </div>
      <div className="bg-card p-1.5 rounded border border-border">
        <p className="font-semibold text-foreground text-[10px]">342</p>
        <p className="text-[7px] text-muted-foreground">Conv.</p>
      </div>
    </div>
    <div className="text-[8px] text-muted-foreground pt-1.5 border-t border-border">
      3 notifications
    </div>
  </div>
);

// Desktop dashboard with removals
const SimplifiedDesktop = () => (
  <div className="hidden md:block bg-zone-diagnosis rounded-md p-4 border border-border">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="font-heading font-semibold text-foreground text-sm">Dashboard</span>
        <Faded><span className="text-[10px] bg-problem-light px-1.5 py-0.5 rounded-full">Pro</span></Faded>
      </div>
      <div className="flex items-center gap-1">
        <button className="p-1.5 text-muted-foreground hover:bg-secondary rounded"><Search size={13} /></button>
        <Faded><button className="p-1.5 text-muted-foreground"><Bell size={13} /></button></Faded>
        <button className="p-1.5 text-muted-foreground hover:bg-secondary rounded"><Bell size={13} /></button>
        <button className="px-2 py-1 bg-primary text-primary-foreground text-[10px] rounded flex items-center gap-0.5">
          <Plus size={10} />New
        </button>
        <Faded><button className="px-2 py-1 bg-secondary text-[10px] rounded">Export</button></Faded>
      </div>
    </div>

    <div className="grid grid-cols-6 gap-2 mb-3">
      {/* Kept metrics */}
      <div className="dashboard-card p-2">
        <DollarSign size={11} className="text-muted-foreground mb-0.5" />
        <p className="font-heading font-semibold text-foreground text-sm">$24,521</p>
        <p className="text-[9px] text-muted-foreground">Revenue</p>
      </div>
      <div className="dashboard-card p-2">
        <Users size={11} className="text-muted-foreground mb-0.5" />
        <p className="font-heading font-semibold text-foreground text-sm">1,429</p>
        <p className="text-[9px] text-muted-foreground">Users</p>
      </div>
      {/* Removed metrics */}
      <Faded>
        <div className="dashboard-card p-2">
          <Activity size={11} className="text-muted-foreground mb-0.5" />
          <p className="font-semibold text-sm">8,942</p>
          <p className="text-[9px]">Sessions</p>
        </div>
      </Faded>
      <Faded>
        <div className="dashboard-card p-2">
          <TrendingUp size={11} className="text-muted-foreground mb-0.5" />
          <p className="font-semibold text-sm">42.1%</p>
          <p className="text-[9px]">Bounce</p>
        </div>
      </Faded>
      <Faded>
        <div className="dashboard-card p-2">
          <TrendingUp size={11} className="text-muted-foreground mb-0.5" />
          <p className="font-semibold text-sm">4m 23s</p>
          <p className="text-[9px]">Duration</p>
        </div>
      </Faded>
      {/* Kept */}
      <div className="dashboard-card p-2">
        <TrendingUp size={11} className="text-muted-foreground mb-0.5" />
        <p className="font-heading font-semibold text-foreground text-sm">342</p>
        <p className="text-[9px] text-muted-foreground">Conversions</p>
      </div>
    </div>

    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2 dashboard-card p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-foreground">Analytics</span>
          <div className="flex gap-0.5">
            <Faded><button className="px-1.5 py-0.5 text-[9px] bg-secondary rounded">Day</button></Faded>
            <button className="px-1.5 py-0.5 text-[9px] bg-primary text-primary-foreground rounded">Week</button>
            <button className="px-1.5 py-0.5 text-[9px] bg-secondary rounded">Month</button>
          </div>
        </div>
        <div className="flex items-end justify-between h-16 gap-1">
          {[65, 45, 78, 52, 88, 42, 95, 67, 73, 58, 82, 48].map((h, i) => (
            <div key={i} className="flex-1 bg-primary/20 rounded-t-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Faded>
          <div className="dashboard-card p-2">
            <span className="text-[9px] font-medium">Quick Actions</span>
            <div className="grid grid-cols-2 gap-1 mt-1">
              <button className="text-[9px] p-1 bg-secondary rounded">Add</button>
              <button className="text-[9px] p-1 bg-secondary rounded">Report</button>
            </div>
          </div>
        </Faded>
        <div className="dashboard-card p-2">
          <span className="text-[9px] font-medium text-muted-foreground">Activity</span>
          <div className="mt-1 space-y-0.5">
            {["User signed up", "Payment received"].map((a, i) => (
              <div key={i} className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />{a}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    <div className="mt-3 pt-2 border-t border-border flex items-center justify-between">
      <Faded>
        <div className="flex gap-3 text-[10px]">
          <span>3 urgent</span><span>5 warnings</span>
        </div>
      </Faded>
      <span className="text-[10px] text-muted-foreground">3 notifications</span>
    </div>
  </div>
);

const RemovalItem = ({ title, desc }) => (
  <div className="flex items-start gap-2">
    <div className="w-4 h-4 rounded-full bg-problem-light flex items-center justify-center flex-shrink-0 mt-0.5">
      <X size={10} className="text-problem" />
    </div>
    <div>
      <p className="font-medium text-foreground text-sm">{title}</p>
      <p className="text-xs text-muted-foreground">{desc}</p>
    </div>
  </div>
);

export const RemovalSection = () => {
  const removals = [
    { title: "Secondary metrics", desc: "Moved to reports" },
    { title: "Redundant buttons", desc: "Consolidated" },
    { title: "Quick Actions", desc: "Via Cmd+K" },
    { title: "Urgent badges", desc: "Single center" }
  ];

  return (
    <section id="removal" className="zone-resolution section-tight border-t border-border">
      <div className="container-wide">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-5">
          <p className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase mb-1.5">Simplification</p>
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-2">
            What We Remove First
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl">
            We do not add solutions yet. We remove noise first.
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
          <SimplifiedMobile />
          <SimplifiedDesktop />
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-5">
          <div className="bg-card rounded-md border border-border p-4 max-w-md">
            <h4 className="font-heading font-semibold text-foreground text-sm mb-3">Removed</h4>
            <div className="grid grid-cols-2 gap-3">
              {removals.map((r, i) => <RemovalItem key={i} {...r} />)}
            </div>
            <p className="text-xs text-muted-foreground mt-4 pt-3 border-t border-border italic">
              Nothing removed was useless — it was just not essential.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RemovalSection;
